import type { SeatUsage } from "../../organizations/types";
import { AlertTriangle, XCircle, Mail, ArrowUpCircle } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";

interface SeatCapacityBannerProps {
  seatUsage: SeatUsage;
  organizationName?: string;
  /** When true, show an "Upgrade" button (for SEED/SCALE tiers) */
  showUpgrade?: boolean;
}

/**
 * Warning / critical / full banner displayed above the roster table
 * when the organization is approaching or has reached its seat limit.
 */
export function SeatCapacityBanner({
  seatUsage,
  organizationName,
  showUpgrade,
}: SeatCapacityBannerProps) {
  const { t } = useLocale();
  const { status, used, limit, percentage } = seatUsage;

  // Only render for WARNING, CRITICAL, or FULL
  if (status === "OK" || status === "UNLIMITED") return null;

  const isFull = status === "FULL";
  const isCritical = status === "CRITICAL";

  const borderColor = isFull || isCritical
    ? "border-destructive/30"
    : "border-amber-500/30";
  const bgColor = isFull || isCritical
    ? "bg-destructive/5"
    : "bg-amber-500/5";
  const textColor = isFull || isCritical
    ? "text-destructive"
    : "text-amber-600 dark:text-amber-400";
  const Icon = isFull ? XCircle : AlertTriangle;

  const message = isFull
    ? t("seats.limitReached", { used: String(used), limit: String(limit) })
    : isCritical
      ? t("seats.almostFull", { used: String(used), limit: String(limit), percentage: String(Math.round(percentage ?? 0)) })
      : t("seats.approaching", { used: String(used), limit: String(limit), percentage: String(Math.round(percentage ?? 0)) });

  const orgParam = organizationName
    ? encodeURIComponent(organizationName)
    : "";
  const mailtoHref = `mailto:sales@kommit.app?subject=Seat%20Limit%20Upgrade${orgParam ? `%20for%20${orgParam}` : ""}`;

  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm ${borderColor} ${bgColor}`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${textColor}`} />
      <span className={`flex-1 ${textColor}`}>{message}</span>
      {showUpgrade && (
        <a
          href={`mailto:sales@kommit.app?subject=${encodeURIComponent(`Upgrade Request${orgParam ? ` for ${decodeURIComponent(orgParam)}` : ""}`)}`}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <ArrowUpCircle className="h-3.5 w-3.5" />
          {t("common.upgrade")}
        </a>
      )}
      <a
        href={mailtoHref}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        <Mail className="h-3.5 w-3.5" />
        {t("common.contactSales")}
      </a>
    </div>
  );
}
